import { useMemo } from 'react'
import { Stack, Tooltip, Typography } from '@mui/material'

import { CardActivityType } from '@/models/card-activity/card-activity.model'
import { CARD_ICONS } from '@/constants/card-icons.constants'

function CardTypeChip({
  type = CardActivityType.REPEAT,
  count = 0,
  showTooltip = false,
  size = 'small', // size can be 'small', 'medium', or 'large'
  iconOnly = false,
  borderRadius = '100px',
}) {
  const configMap = useMemo(
    () => ({
      [CardActivityType.REPEAT]: {
        label: 'Repeat',
        icon: CARD_ICONS.RepeatCard,
        backgroundColor: '#ECFDFF',
        borderColor: '#A5F0FC',
        color: '#026AA2',
      },
      [CardActivityType.READ_REPEAT]: {
        label: 'Repeat',
        icon: CARD_ICONS.RepeatCard,
        backgroundColor: '#ECFDFF',
        borderColor: '#A5F0FC',
        color: '#026AA2',
      },
      [CardActivityType.MULTIPLE_CHOICE]: {
        label: 'Multiple Choice',
        icon: CARD_ICONS.MCCard,
        backgroundColor: '#F4F3FF',
        borderColor: '#D9D6FE',
        color: '#5925DC',
      },
      [CardActivityType.RESPOND]: {
        label: 'Respond',
        icon: CARD_ICONS.RespondCardSpoken,
        backgroundColor: '#EFF8FF',
        borderColor: '#B2DDFF',
        color: '#175CD3',
      },
      [CardActivityType.READ_RESPOND]: {
        label: 'Respond',
        icon: CARD_ICONS.RespondCardSpoken,
        backgroundColor: '#EFF8FF',
        borderColor: '#B2DDFF',
        color: '#175CD3',
      },
      [CardActivityType.FREE_RESPONSE]: {
        label: 'Respond',
        icon: CARD_ICONS.RespondCardSpoken,
        backgroundColor: '#EFF8FF',
        borderColor: '#B2DDFF',
        color: '#175CD3',
      },
      [CardActivityType.RESPOND_WRITE]: {
        label: 'Respond',
        icon: CARD_ICONS.RespondCardWritten,
        backgroundColor: '#FAFDF7',
        borderColor: '#CEEAB0',
        color: '#067647',
      },
      [CardActivityType.SHORT_ANSWER]: {
        label: 'Short Answer',
        icon: CARD_ICONS.ShortAnswer,
        backgroundColor: '#FAFDF7',
        borderColor: '#CEEAB0',
        color: '#067647',
      },
      [CardActivityType.MEDIA_PAGE]: {
        label: 'Media Page',
        icon: CARD_ICONS.MediaPage,
        backgroundColor: '#FFFAEB',
        borderColor: '#FDB022',
        color: '#DC6803',
      },
      default: {
        label: 'Default',
        icon: null,
        backgroundColor: '#E0E0E0',
        borderColor: '#BDBDBD',
        color: '#757575',
      },
    }),
    [],
  )

  const sizeConfig = {
    small: {
      iconSize: iconOnly ? 20 : 16, // Slightly larger icon for iconOnly
      chipPadding: iconOnly ? '2px' : '2px 8px 2px 3px',
      fontSize: '12px',
      chipHeight: iconOnly ? 24 : 24,
    },
    medium: {
      iconSize: iconOnly ? 26 : 20,
      chipPadding: iconOnly ? '4px' : '4px 10px 4px 5px',
      fontSize: '14px',
      chipHeight: iconOnly ? 28 : 32,
    },
    large: {
      iconSize: iconOnly ? 32 : 28,
      chipPadding: iconOnly ? '6px' : '6px 12px 6px 7px',
      fontSize: '16px',
      chipHeight: iconOnly ? 36 : 40,
    },
  }

  // Get size-specific styles
  const { iconSize, chipPadding, fontSize, chipHeight } = sizeConfig[size]

  // Get the type-specific styles from the map
  const { label, icon: Icon, backgroundColor, borderColor, color } = configMap[type] || configMap.default

  return (
    <Tooltip
      arrow
      aria-hidden="true"
      disableHoverListener={!showTooltip}
      placement="top"
      title={`${count || ''} ${label}`}>
      <Stack
        alignItems="center"
        direction="row"
        sx={{
          height: iconOnly ? iconSize : chipHeight,
          width: iconOnly ? iconSize : 'max-content',
          borderRadius: borderRadius,
          gap: '4px',
          backgroundColor: iconOnly ? 'transparent' : backgroundColor,
          ...(!iconOnly && {
            backgroundColor,
            border: `1px solid ${borderColor}`,
            padding: chipPadding,
          }),
        }}>
        {Icon ? <Icon style={{ height: iconSize, width: iconSize, flexShrink: 0 }} /> : null}
        {!iconOnly && (
          <Typography
            sx={{
              padding: 0,
              lineHeight: '18px',
              fontSize,
              color,
              fontWeight: 500,
            }}>
            {label}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  )
}

export default CardTypeChip
